import { useState } from "react";
import MultiInput from "components/input/multi";
import { useMutation, useQueryClient } from "react-query";
import { Button } from "components/button";
import { useTranslation } from "react-i18next";
import { useModalContext } from "components/modal";
import { useSnackbarContext } from "components/snackbar";
import axios from "axios";

interface IProps {
  servicePlanId: string;
  propertyId: string;
  customerEmail: string;
  customerEmailsSaved?: string[]; // saved emails from previous send
  customerId: string;
  type: string;
  startDate?: string
};

const ConfirmSendModal = ({ servicePlanId, propertyId, customerEmail, customerEmailsSaved, customerId, type, startDate }: IProps) => {
  const { t } = useTranslation();
  const tBase = "views.service-plans.components.ConfirmSendModal";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const queryClient = useQueryClient();
  const { setModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  const [emails, setEmails] = useState<string[]>(customerEmailsSaved || [customerEmail]);

  const sendContract = useMutation(
    async (payload: { 
      plans: [{
        servicePlanId: string; 
        customerId: string; 
        propertyId: string;
        emails: string[];
        startDate: string;
      }]
    }) => {
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/add-plans/${type}`,
        payload 
      );
      return res.data; 
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["customerPlans", customerId, "plans"]);
        queryClient.invalidateQueries("plans");
        queryClient.invalidateQueries(["customer", customerId]);
        showSnackbar(tr("Contract sent to customer")); 
      },
      onError: (error) => {
        console.error("Error sending contract:", error);
        showSnackbar(tr("An error occurred while adding the plan"));
      },
    }
  );

  return (
    <div className="w-[420px] px-3">
      <p className="m-auto mb-2 text-gray-700">
        {tr("Enter the email(s) to send this contract to")}:
      </p>
      <MultiInput
        values={emails}
        onChange={(e) => setEmails(e.filter((email): email is string => email !== null))}
      />
      <div className="m-auto mt-12 mb-3 w-full flex justify-between items-center">
        <Button
          type="secondary"
          onClick={() => setModal()}
        >
          {tr("Cancel")}
        </Button>
        <Button
          primary
          onClick={() => {
            sendContract.mutateAsync({
              plans: [
                  {
                    servicePlanId,
                    customerId,
                    propertyId,
                    emails,
                    startDate: startDate ?? "",
                  },
                ],
            });
            setModal();
          }}
        >
          {tr("Send")}
        </Button>
      </div>
    </div>
  );
};

export default ConfirmSendModal;